import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Label } from 'recharts';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'redux/store';
import { setSelectedToken, setSelectedContract } from '../../redux/userReducer';
import styles from './PortfolioDonut.module.sass';
import PortfolioScoreModal from '../Modal/PortfolioScore';

interface DonutProps {
  size?: string;
  onSelect?: (symbol: string) => void;
}

const CustomLabel = ({ viewBox, value, name }: any) => {
  const { cx, cy } = viewBox;
  return (
    <text 
      x={cx} 
      y={cy} 
      className="recharts-text recharts-label" 
      textAnchor="middle" 
      dominantBaseline="central"
    >
      <tspan 
        x={cx}
        y={cy - 10}
        fill="#73F6FF"
        fontSize="24"
        fontWeight="bold"
        fontFamily="monospace"
        alignmentBaseline="middle"
      >
        {value}%
      </tspan>
      <tspan
        x={cx}
        y={cy + 15}
        fill="#73F6FF"
        fillOpacity="0.8"
        fontSize="14"
        fontFamily="monospace"
        alignmentBaseline="middle"
      >
        {name}
      </tspan>
    </text>
  );
};

const PortfolioDonut = ({ size = 'max-w-[200px]', onSelect }: DonutProps) => {
  const dispatch = useDispatch();
  const holdings = useSelector((state: RootState) => 
    Array.isArray(state.portfolio.holdings) ? state.portfolio.holdings : []
  );
  
  // Score selectors
  const marketScore = useSelector((state: RootState) => state.portfolio.marketScore);
  const portfolioScore = useSelector((state: RootState) => state.portfolio.portfolioScore);

  // Loading state selectors
  const isPortBenchLoading = useSelector((state: RootState) => state.portfolio.loading.portBench);
  const isMaxDrawDownLoading = useSelector((state: RootState) => state.portfolio.loading.maxDrawDown);

  const [selectedSegment, setSelectedSegment] = useState<string | null>(() => {
    const sortedHoldings = [...holdings].sort((a, b) => b.portfolio_percentage - a.portfolio_percentage);
    return sortedHoldings[0]?.symbol || null;
  });
  
  // Add state for modal visibility
  const [isScoreModalOpen, setIsScoreModalOpen] = useState(false);

  const calculatePortfolioScore = () => {
    // Check if metrics are still loading
    if (isPortBenchLoading || isMaxDrawDownLoading) {
      return null;
    }

    // Default scores to 3 if null
    const market = marketScore ?? 3;
    const drawdown = portfolioScore ?? 3;

    // Equal weighting of both scores (50% each)
    return (market + drawdown) / 2;
  };

  const aggregateScore = calculatePortfolioScore();
  
  const getScoreColor = (score: number) => {
    if (score < 2) return '#f44336';  // Red
    if (score < 3.5) return '#9c27b0'; // Purple
    return '#73F6FF';  // Cyan
  };

  const data = [...holdings]
    .sort((a, b) => b.portfolio_percentage - a.portfolio_percentage)
    .map(holding => ({
      name: holding.symbol,
      value: holding.portfolio_percentage,
      usdValue: holding.usd_value,
      token_address: holding.token_address,
      chain: holding.chain || 'ethereum'
    }));

  const [isPopupOpen, setIsPopupOpen] = useState(true);

  useEffect(() => {
    setIsPopupOpen(true);
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setSelectedToken(null));
      dispatch(setSelectedContract({ contract: null, chain: null }));
    };
  }, [dispatch]);

  const baseColors = [
    '#4ECDC4CC',
    '#9B59B6CC',
    '#45B7D1CC',
    '#E67E22CC',
    '#3498DBCC',
    '#9B59B666',
    '#2ECC71CC',
    '#E74C3CCC',
    '#34495ECC',
    '#4ECDC466'
  ];

  const handleSegmentClick = (entry: any, index: number) => {
    const symbol = entry.name;
    const newSelection = selectedSegment === symbol ? null : symbol;
    setSelectedSegment(newSelection);
    
    if (newSelection === null) {
      dispatch(setSelectedToken(null));
      dispatch(setSelectedContract({ contract: null, chain: null }));
    } else {
      dispatch(setSelectedToken(newSelection));
      dispatch(setSelectedContract({ 
        contract: entry.token_address,
        chain: entry.chain || 'ethereum'
      }));
    }
    
    onSelect?.(symbol);
  };

  const selectedData = selectedSegment 
    ? data.find(d => d.name === selectedSegment)
    : data[0];

  return (
    <div className={styles.container}>
      <div className={styles.donutWrapper}>
        <div className="absolute inset-0 bg-[#73F6FF]/5 blur-xl rounded-full opacity-50 mix-blend-overlay pointer-events-none" />
        
        <PieChart width={200} height={200}>
          <Pie
            data={[{ value: 100 }]}
            cx={100}
            cy={100}
            innerRadius={65}
            outerRadius={92}
            fill="#17171E"
            stroke="#73F6FF"
            strokeWidth={0.5}
            strokeOpacity={0.1}
          />
          
          <Pie
            data={data}
            cx={100}
            cy={100}
            innerRadius={68}
            outerRadius={90}
            paddingAngle={1}
            dataKey="value"
            stroke="#17171E"
            strokeWidth={1}
            onClick={handleSegmentClick}
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={baseColors[index % baseColors.length]}
                className="transition-all duration-300 hover:brightness-125"
                style={{
                  transform: selectedSegment === entry.name ? 'scale(1.05) translateY(-2px)' : 'scale(1)',
                  filter: selectedSegment === entry.name ? 'brightness(1.2) drop-shadow(0 0 4px currentColor)' : 'none',
                  transformOrigin: '100px 100px',
                  cursor: 'pointer'
                }}
              />
            ))}
            <Label
              width={30}
              position="center"
              content={<CustomLabel 
                value={selectedData?.value.toFixed(1)} 
                name={selectedData?.name}
              />}
            />
          </Pie>
        </PieChart>

        <svg width="0" height="0">
          <filter id="grain">
            <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#grain)" opacity="0.15" />
        </svg>

        <div className="absolute inset-0 rounded-full border border-[#73F6FF]/10" />
      </div>

      <div 
        className={styles.scoreMetric}
        onClick={() => setIsScoreModalOpen(true)}
        style={{ 
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
          e.currentTarget.style.borderColor = 'rgba(115, 246, 255, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.borderColor = 'rgba(115, 246, 255, 0.1)';
        }}
      >
        <div className={styles.metricContent}>
          <div className={styles.metricStats}>
            <div className={styles.metricLabel}>Portfolio Score <span style={{ fontSize: '9px', opacity: 0.7 }}>ⓘ</span></div>
            <div className={styles.scoreContent}>
              <div className={styles.scoreSegments}>
                {[...Array(5)].map((_, i) => {
                  const score = aggregateScore ?? 0;
                  const isFullyFilled = aggregateScore ? (i < Math.floor(score)) : false;
                  const isPartiallyFilled = aggregateScore ? (i === Math.floor(score)) : false;
                  const partialFillPercentage = isPartiallyFilled ? (score % 1) * 100 : 0;
                  
                  let segmentStyle = {};
                  if (isFullyFilled) {
                    segmentStyle = {
                      background: getScoreColor(score),
                      boxShadow: `0 0 10px ${getScoreColor(score)}40`
                    };
                  } else if (isPartiallyFilled && partialFillPercentage > 0) {
                    segmentStyle = {
                      background: `linear-gradient(to right, ${getScoreColor(score)} ${partialFillPercentage}%, rgba(115, 246, 255, 0.1) ${partialFillPercentage}%)`,
                      boxShadow: `0 0 10px ${getScoreColor(score)}40`
                    };
                  }
                  
                  return (
                    <div 
                      key={i}
                      className={`${styles.segment} ${isFullyFilled ? styles.segmentFilled : ''}`}
                      style={segmentStyle}
                    />
                  );
                })}
              </div>
              <div 
                className={styles.metricValue}
                style={{ 
                  color: aggregateScore ? getScoreColor(aggregateScore) : '#73F6FF',
                  textShadow: aggregateScore ? `0 0 10px ${getScoreColor(aggregateScore)}40` : undefined
                }}
              >
                {aggregateScore ? `${aggregateScore.toFixed(2)}/5.00` : 'Loading...'}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add the Portfolio Score Modal */}
      <PortfolioScoreModal
        open={isScoreModalOpen}
        onClose={() => setIsScoreModalOpen(false)}
        score={aggregateScore ?? 3}
        marketScore={marketScore ?? 3}
        drawdownScore={portfolioScore ?? 3}
      />
    </div>
  );
};

export default PortfolioDonut;